type SupportedLang = 'ru' | 'kk';

const RU_ONES_M = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const RU_ONES_F = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const RU_TEENS = [
  'десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать',
  'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать',
];
const RU_TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
const RU_HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];

interface RuScale {
  one: string;
  few: string;
  many: string;
  gender: 'm' | 'f';
}

// Индекс — номер группы по 3 цифры (0 = единицы, 1 = тысячи, ...).
const RU_SCALES: Record<number, RuScale> = {
  1: {one: 'тысяча', few: 'тысячи', many: 'тысяч', gender: 'f'},
  2: {one: 'миллион', few: 'миллиона', many: 'миллионов', gender: 'm'},
  3: {one: 'миллиард', few: 'миллиарда', many: 'миллиардов', gender: 'm'},
  4: {one: 'триллион', few: 'триллиона', many: 'триллионов', gender: 'm'},
};

function ruPluralForm(n: number, forms: { one: string; few: string; many: string }): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) {
    return forms.many;
  }
  if (mod10 === 1) {
    return forms.one;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return forms.few;
  }
  return forms.many;
}

function ruGroupToWords(n: number, gender: 'm' | 'f'): string[] {
  const words: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) {
    words.push(RU_HUNDREDS[hundreds]);
  }
  if (rest >= 10 && rest <= 19) {
    words.push(RU_TEENS[rest - 10]);
  } else {
    const tens = Math.floor(rest / 10);
    const ones = rest % 10;
    if (tens >= 2) {
      words.push(RU_TENS[tens]);
    }
    if (ones) {
      words.push(gender === 'f' ? RU_ONES_F[ones] : RU_ONES_M[ones]);
    }
  }
  return words;
}

function toGroups(value: number): number[] {
  const groups: number[] = [];
  let n = value;
  do {
    groups.push(n % 1000);
    n = Math.floor(n / 1000);
  } while (n > 0);
  return groups;
}

function numberToWordsRu(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  value = Math.trunc(Math.abs(value));
  if (value === 0) {
    return 'ноль';
  }

  const groups = toGroups(value);
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) {
      continue;
    }
    const scale = RU_SCALES[i];
    const gender: 'm' | 'f' = scale ? scale.gender : 'm';
    parts.push(...ruGroupToWords(g, gender));
    if (scale) {
      parts.push(ruPluralForm(g, scale));
    }
  }
  return parts.join(' ');
}

const KK_ONES = ['', 'бір', 'екі', 'үш', 'төрт', 'бес', 'алты', 'жеті', 'сегіз', 'тоғыз'];
const KK_TENS = ['', 'он', 'жиырма', 'отыз', 'қырық', 'елу', 'алпыс', 'жетпіс', 'сексен', 'тоқсан'];
const KK_HUNDRED = 'жүз';
// Индекс — номер группы по 3 цифры, как в RU_SCALES.
const KK_SCALES: Record<number, string> = {
  1: 'мың',
  2: 'миллион',
  3: 'миллиард',
  4: 'триллион',
};

function kkGroupToWords(n: number): string[] {
  const words: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) {
    if (hundreds > 1) {
      words.push(KK_ONES[hundreds]);
    }
    words.push(KK_HUNDRED);
  }
  const tens = Math.floor(rest / 10);
  const ones = rest % 10;
  if (tens) {
    words.push(KK_TENS[tens]);
  }
  if (ones) {
    words.push(KK_ONES[ones]);
  }
  return words;
}

function numberToWordsKk(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  value = Math.trunc(Math.abs(value));
  if (value === 0) {
    return 'нөл';
  }

  const groups = toGroups(value);
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) {
      continue;
    }
    const scale = KK_SCALES[i];
    // "мың"/"миллион" и т.д. без "бір": 1000 -> "мың", а не "бір мың".
    if (!(scale && g === 1)) {
      parts.push(...kkGroupToWords(g));
    }
    if (scale) {
      parts.push(scale);
    }
  }
  return parts.join(' ');
}

// RU_SCALES/KK_SCALES заканчиваются на триллионах (группа 4), поэтому пропись
// корректна максимум до 999 999 999 999 999 — с 16-й цифры старшие группы
// теряются (1 000 000 000 000 000 -> "один"). Ввод больше этого режем.
export const MAX_NUMBER_TO_WORDS_DIGITS = 15;

export function numberToWords(value: number, lang: SupportedLang | string): string {
  return lang === 'kk' ? numberToWordsKk(value) : numberToWordsRu(value);
}

// "20000" -> "20 000"
export function groupThousands(digits: string): string {
  if (!digits) {
    return '';
  }
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
